import { type FC, useState } from 'react'
import { ModalDialog } from '../../common/ModalDialog.tsx'
import { t } from 'i18next'
import Button from '../../common/Button.tsx'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { deleteMediaFile } from '@/features/media-file/domain/use-cases/delete-media-file.ts'
import { QueryKeys } from '@/utils/query-keys.ts'
import type { UUID } from '@/features/shared/data/string.types.ts'

interface DeleteFileConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  fileId: UUID
  folderId: UUID
}

const DeleteFileConfirmationDialog: FC<DeleteFileConfirmationDialogProps> = ({
  isOpen,
  onClose,
  fileId,
  folderId,
}) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const queryClient = useQueryClient()

  const onDeleteFile = useMutation({
    mutationFn: async ({ id }: { id: UUID }): Promise<void> => {
      setIsDeleting(true)
      await deleteMediaFile(id, folderId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MediaFolderContent],
      })
      onClose()
      setIsDeleting(false)
    },
    onError: (err) => {
      console.error('Error deleting media file', err)
      onClose()
      setIsDeleting(false)
    },
  })

  return (
    <ModalDialog
      open={isOpen}
      title={'ls_delete_file_confirm_title'}
      onClose={onClose}
      className="max-w-[416px]"
      actions={
        <>
          <Button onClick={onClose}>{t('ls_generic_cancel')}</Button>
          <Button
            onClick={() => onDeleteFile.mutate({ id: fileId })}
            warning
            isLoading={isDeleting}
          >
            {t('ls_generic_delete')}
          </Button>
        </>
      }
    >
      <span className="text-sm">{t('ls_delete_file_confirm_message')}</span>
    </ModalDialog>
  )
}

export default DeleteFileConfirmationDialog
